/**
 * 「今天要做什么」的分组 —— **一条打卡只能渲染一次**。
 *
 * ## 它锁住的是 2026-10-17 用户实测的 bug（孩子端出现"重复的打卡记录"）
 *
 * 用户原话：
 *
 * > 孩子提交一条完成之后，会出现**重复的那些打卡记录**。
 *
 * 截图（孩子端 · 儿童模式 · 2026-10-17 周六）：`听写（下午）` 出现 **2 次**、
 * `听写（上午）` 出现 **2 次**，而 `计算练习` 只有 1 次。
 *
 * ### 根因：分组的 React key 用了**显示名**，而显示名不唯一
 *
 * 页面原来是这样渲染的（历史代码）：
 *
 * ```tsx
 * // 分组按 nodeId（唯一）……
 * const key = task.nodeId || task.goalName || '未分组';
 * // ……但交给 React 的 key 却是 label（= 上级目标名）
 * <div key={group.name}>            // ⛔ group.name 可以重复
 * ```
 *
 * `group.name` 是**上级目标名**（`goalName`），而一个目标下面可以挂**多个**
 * 子节点（"认字和写字"下面同时有"练字"和"节假日听写"）。生产实测（Harry，
 * 2026-10-17）当天 9 条打卡按节点分成 **8 组**，其中：
 *
 * | 分组标签（旧 key） | 节点 | 条数 |
 * | --- | --- | --- |
 * | `认字和写字` | 练字 | 1 |
 * | `认字和写字` | 节假日听写（听写上午 + 听写下午） | 2 |
 * | `提升数学成绩` | 计算能力（计算练习） | 1 |
 * | `提升数学成绩` | 数学思维（斑马思维） | 1 |
 *
 * 于是同一层里出现两个 `key="认字和写字"` 的兄弟节点。React 对重复 key 的
 * 处理是**未定义行为**，它自己的告警原文就写着：
 *
 * > Encountered two children with the same key. Keys should be unique so that
 * > components maintain their identity across updates. **Non-unique keys may
 * > cause children to be duplicated and/or omitted.**
 *
 * ——"duplicated and/or omitted" 正是用户看到的形态：那一组（2 条）被渲染了
 * 两遍（2 × 2 = 4 张卡），而另一组（练字）被丢掉。
 *
 * ### 为什么"完成之后"才出现
 *
 * **首次挂载不做 key 匹配**（没有旧树可比），所以刚打开页面是好的；
 * 孩子点「完成」→ 打卡状态变了 → `items.sort(STATUS_ORDER)` 让分组顺序变化
 * → 触发一次**带 key 匹配的更新**，重复 key 才会咬人。这就是用户说的
 * "**之前是好的，提交一条完成之后就重复了**"。
 *
 * ### 修法（两条，缺一不可）
 *
 * 1. **key 用分组的身份而不是显示名**：`group.key` 就是分组时用的那个键
 *    （节点 id；退化时是目标名/`未分组`），它对**同一层内的不同组**天然唯一
 *    —— 显示名可以重复，身份不能（见 `groupKeyOf`）。
 * 2. **按 `checkinId` 去重**：同一张卡在数组里出现两次也只渲染一次。
 *    这一条是**幂等兜底**：将来谁再往 `items` 里 join 出一行重复（物化、
 *    restore、补卡、队列拼接……），孩子看到的仍然是恰好一条。
 *
 * 两条都有单测（`todayGroups.test.ts`），而且判据是这个纯函数**唯一一份**
 * —— 页面不再自己写分组循环（写两处就会再次漂移，这正是本 bug 的成因）。
 */
import type { TodayTaskDetail } from './api/types';

/** 既没有节点 id 也没有目标名时的兜底分组。 */
export const TODAY_UNGROUPED_KEY = '未分组';

/** 既没有目标名时的分组显示名。 */
export const TODAY_UNGROUPED_LABEL = '今天的事';

export interface TodayGroup {
  /**
   * **分组的身份**，用作 React key。
   *
   * 与 `label` 的区别是这条 bug 的全部教训：`label` 给人看（可重复），
   * `key` 给 React 认（同一层内**绝不重复**）。
   * 由 `groupKeyOf()` 保证：节点 id 唯一；退化到目标名时，同名者本来就该并成
   * 一组；再退化是同一个常量。
   */
  key: string;
  /** 显示名（分组标题）。可以重复 —— 所以它不能当 key。 */
  label: string;
  /** 本组的打卡（按输入顺序，同一 `checkinId` 只出现一次）。 */
  items: TodayTaskDetail[];
}

/**
 * 一条打卡属于哪一组。
 *
 * 顺序与历史行为一致（`nodeId` → `goalName` → `未分组`），只是现在它**同时**
 * 充当 React key。三档都是"同一档内相等的元素应当并组"的语义，因此
 * **不同组之间不可能取到同一个值**：
 *  - 节点 id 是 uuid；
 *  - 退化到目标名时，同名的两条本来就会并成同一组；
 *  - 兜底常量只会有唯一一组。
 */
function groupKeyOf(task: TodayTaskDetail): string {
  return task.nodeId || task.goalName || TODAY_UNGROUPED_KEY;
}

/**
 * 把今日清单分组（纯函数，无副作用，幂等）。
 *
 * 保证：
 *  - **每个 `checkinId` 至多出现一次**（重复的后续项直接忽略，保留第一次的位置）；
 *  - 每组的 `key` 在同一层内唯一（见 `groupKeyOf`）；
 *  - 组内顺序 = 输入顺序；组间顺序 = 该组**第一次**出现的位置
 *    （所以服务端 `items` 的排序仍然决定孩子的阅读顺序）；
 *  - 同一份输入反复调用结果完全一致（不会累积状态）。
 */
export function groupTodayItems(items: readonly TodayTaskDetail[]): TodayGroup[] {
  const seenCheckins = new Set<string>();
  const byKey = new Map<string, TodayGroup>();
  const groups: TodayGroup[] = [];

  for (const task of items) {
    // 幂等：同一张卡（同一个 checkinId）只出现一次
    if (seenCheckins.has(task.checkinId)) continue;
    seenCheckins.add(task.checkinId);

    const key = groupKeyOf(task);
    const existing = byKey.get(key);
    if (existing) {
      existing.items.push(task);
      continue;
    }
    const group: TodayGroup = {
      key,
      label: task.goalName || TODAY_UNGROUPED_LABEL,
      items: [task],
    };
    byKey.set(key, group);
    groups.push(group);
  }

  return groups;
}

/** 扁平化回一维（**已去重**）—— "完成了几项 / 还有几项" 一律用它数。 */
export function flattenTodayGroups(groups: readonly TodayGroup[]): TodayTaskDetail[] {
  return groups.flatMap((group) => group.items);
}

/**
 * 今日清单的**去重视图**：同一个 `checkinId` 只留第一条。
 *
 * 页面的进度数字（`X / Y 项`）必须与列表里的卡片数对得上 —— 两处用同一份
 * 去重结果，就不会出现"显示 11 项、列表只有 9 张卡"。
 */
export function uniqueTodayItems(items: readonly TodayTaskDetail[]): TodayTaskDetail[] {
  const seen = new Set<string>();
  const unique: TodayTaskDetail[] = [];
  for (const task of items) {
    if (seen.has(task.checkinId)) continue;
    seen.add(task.checkinId);
    unique.push(task);
  }
  return unique;
}
